import React, { ReactNode, useCallback, useContext, useState } from "react";
import useErrorModal from "../Hooks/useErrorModal";

interface UIContextType {
  showTextbauSteine: boolean;
  setShowTextbauSteine: (show: boolean) => void;
  showHistorie: boolean;
  setShowHistorie: (show: boolean) => void;
  darkTheme: boolean;
  setDarkTheme: (isDark: boolean) => void;
  showDialog: (options: ConfirmationDialogOptions) => void;
  openErrorModal: (message: string, title?: string) => void;
  currentProductID: number;
  setCurrentProductID: (id: number) => void;
  currentPage: string;
  setCurrentPage: (pageTitle: string) => void;
}

interface ConfirmationDialogOptions {
  onConfirm: () => void;
  onCancel?: () => void;
  title: string;
  message: string;
  confirmButtonText: string;
  cancelButtonText?: string;
}

const defaultState: UIContextType = {
  showTextbauSteine: false,
  setShowTextbauSteine: () => {},
  showHistorie: false,
  setShowHistorie: () => {},
  darkTheme: false,
  setDarkTheme: () => {},
  showDialog: () => {},
  openErrorModal: () => {},
  currentProductID: 0,
  setCurrentProductID: () => {},
  currentPage: "",
  setCurrentPage: () => {},
};

const UIContext = React.createContext<UIContextType>(defaultState);

export const useUIContext = () => useContext(UIContext);

interface UIProviderProps {
  children: ReactNode;
}

export const UIProvider: React.FC<UIProviderProps> = ({ children }) => {
  const [showTextbauSteine, setShowTextbauSteine] = useState(
    defaultState.showTextbauSteine
  );
  const [showHistorie, setShowHistorie] = useState(false);
  const [darkTheme, setDarkTheme] = useState(defaultState.darkTheme);
  const [isDialogVisible, setIsDialogVisible] = useState(false);
  const [dialogOptions, setDialogOptions] =
    useState<ConfirmationDialogOptions | null>(null);
  const { openErrorModal, ErrorModal } = useErrorModal();
  const [currentProductID, setCurrentProductID] = useState(0);
  const [currentPage, setCurrentPage] = useState("kundensuche");

  const showDialog = useCallback((options: ConfirmationDialogOptions) => {
    setDialogOptions(options);
    setIsDialogVisible(true);
  }, []);

  const handleDialogClose = useCallback(() => {
    setIsDialogVisible(false);
  }, []);

  const value = {
    showTextbauSteine,
    setShowTextbauSteine,
    showHistorie,
    setShowHistorie,
    darkTheme,
    setDarkTheme,
    showDialog,
    openErrorModal,
    currentProductID,
    setCurrentProductID,
    currentPage,
    setCurrentPage,
  };

  return (
    <UIContext.Provider value={value}>
      <ErrorModal />
      {children}
      {isDialogVisible && dialogOptions && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 flex justify-center items-center">
          <div className="bg-gray-100 rounded-lg p-8 m-4 max-w-xl w-full space-y-4">
            <h2 className="text-center text-lg font-semibold">
              {dialogOptions.title}
            </h2>
            <p className="text-center">{dialogOptions.message}</p>
            <div className="flex justify-evenly">
              {dialogOptions.cancelButtonText !== undefined ? (
                <button
                  className="bg-gray-300 hover:bg-gray-400 text-gray-800 font-bold py-2 px-4 rounded"
                  onClick={() => {
                    dialogOptions.onCancel?.();
                    handleDialogClose();
                  }}
                >
                  {dialogOptions.cancelButtonText}
                </button>
              ) : (
                <></>
              )}
              <button
                className="bg-blue-500 hover:bg-blue-700 text-white font-bold py-2 px-4 rounded"
                onClick={() => {
                  dialogOptions.onConfirm();
                  handleDialogClose();
                }}
              >
                {dialogOptions.confirmButtonText}
              </button>
            </div>
          </div>
        </div>
      )}
    </UIContext.Provider>
  );
};
